import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ValidatorService } from '../../core/services/validator.service';
import { Store } from '@ngrx/store';
import { SaleOfferBeginsService } from '../../core/api';
import { Observable, startWith, tap, withLatestFrom } from 'rxjs';
import { getOfferState } from '../../core/states/offer/offer.selector';
import { first, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNullOrUndefined } from '../../core/utils/typeUtils';
import { FlyoutOption } from 'dap-ui-library';
import { DateService } from '../../core/services/date.service';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-sale-offer-begin-dropdown',
  templateUrl: './sale-offer-begin-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SaleOfferBeginDropdownComponent),
      multi: true,
    },
  ],
})
export class SaleOfferBeginDropdownComponent implements OnInit, ControlValueAccessor {
  @Input() public validateForm: boolean;
  @Output() public saleOfferBeginChanged: EventEmitter<void> = new EventEmitter<void>();

  public saleOfferBegin = new FormControl<string>('', {
    nonNullable: true,
    validators: Validators.required,
  });
  public dates$: Observable<FlyoutOption<string>[]>;

  constructor(
    public validatorService: ValidatorService,
    private store: Store,
    private saleOfferBeginDatesService: SaleOfferBeginsService,
  ) {}

  private static mapToSaleOfferBeginOption(dates: string[]): FlyoutOption<string>[] {
    return dates.map((date) => ({
      label: DateService.formatDateForView(date),
      value: date,
    }));
  }

  public ngOnInit(): void {
    this.dates$ = this.saleOfferBeginDatesService.getSaleOfferBegins().pipe(
      withLatestFrom(this.store.select(getOfferState)),
      first(),
      untilDestroyed(this),
      map(([dates, offerState]) => {
        if (isNullOrUndefined(offerState.saleOfferBegin)) {
          this.saleOfferBegin.setValue(dates.default_sale_offer_begin);
        } else {
          this.saleOfferBegin.setValue(offerState.saleOfferBegin);
        }
        return SaleOfferBeginDropdownComponent.mapToSaleOfferBeginOption(dates.sale_offer_begins);
      }),
      startWith([]),
    );
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.saleOfferBegin.valueChanges
      .pipe(
        tap(() => this.saleOfferBeginChanged.emit()),
        untilDestroyed(this),
      )
      .subscribe(fn);
  }

  public registerOnTouched(fn: (value: string) => void): void {
    this.saleOfferBegin.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
  }

  public writeValue(value: string): void {
    this.saleOfferBegin.setValue(value);
  }
}
